'use client'

import { featureFlagKeys } from 'feature-flags/constants/featureFlags'
import { useFeatureFlag } from 'feature-flags/hooks/useFeatureFlag/useFeatureFlag'
import { REGISTRY_NEWSLETTER_LEGAL_CONFIRMATION_LABEL_KEYS } from 'newsletter/constants/registryLegalConfirmation'
import type { SubscriptionCheckboxProps } from 'newsletter/types/checkbox'
import type { NewsletterConfiguration } from 'newsletter/types/featureFlag'
import dynamic from 'next/dynamic'

import { RegistryOrigin } from '../../../constants/registry'

const LegalConfirmation = dynamic(() =>
	import(
		'legal/components/policies/RegistryLegalPolicy/legal-confirmation/LegalConfirmation'
	).then((mod) => mod.LegalConfirmation)
)

const CheckoutRegistryNewsletterSubscription = dynamic(() =>
	import(
		'newsletter/components/CheckoutRegistryCheckbox/CheckoutRegistryCheckbox'
	).then((mod) => mod.CheckoutRegistryCheckbox)
)

const RegistryNewsletterSubscription = dynamic(() =>
	import('newsletter/components/RegistryCheckbox/RegistryCheckbox').then(
		(mod) => mod.RegistryCheckbox
	)
)

type NewsletterSubscriptionCheckboxProps = SubscriptionCheckboxProps & {
	readonly origin: RegistryOrigin
}

export const NewsletterSubscriptionCheckbox = ({
	origin,
	...subscriptionCheckboxProps
}: NewsletterSubscriptionCheckboxProps) => {
	const isInCheckout = origin === RegistryOrigin.CHECKOUT
	const { enabled: newsletterEnabled, variables: newsletterConfigVariables } =
		useFeatureFlag<NewsletterConfiguration>(
			featureFlagKeys.NewsletterConfiguration
		)

	if (!newsletterEnabled) {
		return null
	}

	const isExpandable = !!newsletterConfigVariables?.isExpandable

	if (isExpandable) {
		return (
			<LegalConfirmation
				{...subscriptionCheckboxProps}
				labelKeys={REGISTRY_NEWSLETTER_LEGAL_CONFIRMATION_LABEL_KEYS}
			/>
		)
	}

	if (isInCheckout) {
		return (
			<CheckoutRegistryNewsletterSubscription {...subscriptionCheckboxProps} />
		)
	}

	return <RegistryNewsletterSubscription {...subscriptionCheckboxProps} />
}
